import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

function NotificationsPanel() {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showNotifications, setShowNotifications] = useState(false);

  useEffect(() => {
    fetchNotifications();
    // Actualiser les notifications toutes les 30 secondes
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchNotifications = async () => {
    try {
      const response = await axios.get('/api/workflow/notifications?limit=20');
      if (response.data.success) {
        setNotifications(response.data.notifications);
        setUnreadCount(response.data.unread_count);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await axios.post(`/api/workflow/notifications/${notificationId}/read`);
      // Mettre à jour localement
      setNotifications(prev => 
        prev.map(notif => 
          notif.id === notificationId 
            ? { ...notif, lu: true }
            : notif
        )
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Erreur lors du marquage comme lu:', error);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'approbation_requise': return '✅';
      case 'formulaire_valide': return '🎉';
      case 'formulaire_rejete': return '❌';
      case 'echeance_proche': return '⏰';
      case 'formulaire_assigne': return '📋';
      default: return '📢';
    }
  };

  const getNotificationColor = (type) => {
    switch (type) {
      case 'approbation_requise': return '#fd7e14';
      case 'formulaire_valide': return '#28a745';
      case 'formulaire_rejete': return '#dc3545';
      case 'echeance_proche': return '#ffc107';
      case 'formulaire_assigne': return '#007bff';
      default: return '#6c757d';
    }
  };

  // 🔧 Décoder les entités HTML (fix sanitisation serveur)
  const decodeHtmlEntities = (text) => {
    if (!text || typeof text !== 'string') return text;
    return text
      .replace(/&#x2F;/g, '/')
      .replace(/&#x3A;/g, ':')
      .replace(/&quot;/g, '"')
      .replace(/&#x27;/g, "'")
      .replace(/&amp;/g, '&');
  };

  const formatDate = (dateString) => {
    const decodedDate = decodeHtmlEntities(dateString);
    const date = new Date(decodedDate);
    const now = new Date();
    const diffMinutes = Math.floor((now - date) / 60000);
    
    if (diffMinutes < 1) return 'À l\'instant';
    if (diffMinutes < 60) return `Il y a ${diffMinutes} min`;
    if (diffMinutes < 1440) return `Il y a ${Math.floor(diffMinutes / 60)} h`;
    return date.toLocaleDateString('fr-FR');
  };

  return (
    <div style={{ position: 'relative' }}>
      {/* Bouton notifications avec badge */}
      <button
        onClick={() => setShowNotifications(!showNotifications)}
        style={{
          position: 'relative',
          background: 'rgba(255,255,255,0.2)',
          border: '1px solid rgba(255,255,255,0.3)',
          color: 'white',
          padding: '8px 12px',
          borderRadius: '5px',
          cursor: 'pointer',
          fontSize: '16px'
        }}
      >
        🔔
        {unreadCount > 0 && (
          <span style={{
            position: 'absolute',
            top: '-5px',
            right: '-5px',
            background: '#dc3545',
            color: 'white',
            borderRadius: '50%',
            width: '20px',
            height: '20px',
            fontSize: '11px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 'bold'
          }}>
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Panel des notifications */}
      {showNotifications && (
        <div style={{
          position: 'absolute',
          top: '100%',
          right: '0',
          width: '400px',
          maxHeight: '500px',
          background: 'white',
          border: '1px solid #dee2e6',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          zIndex: 1000,
          marginTop: '5px'
        }}>
          {/* En-tête */}
          <div style={{
            padding: '15px',
            borderBottom: '1px solid #dee2e6',
            background: '#f8f9fa',
            borderRadius: '8px 8px 0 0'
          }}>
            <h4 style={{ margin: 0, color: '#9cb85c' }}>
              🔔 Notifications ({notifications.length})
            </h4>
            {unreadCount > 0 && (
              <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '5px' }}>
                {unreadCount} non lue{unreadCount > 1 ? 's' : ''}
              </div>
            )}
          </div>

          {/* Liste des notifications */}
          <div style={{
            maxHeight: '400px',
            overflowY: 'auto'
          }}>
            {loading ? (
              <div style={{ padding: '20px', textAlign: 'center', color: '#6c757d' }}>
                🔄 Chargement...
              </div>
            ) : notifications.length === 0 ? (
              <div style={{ padding: '20px', textAlign: 'center', color: '#6c757d' }}>
                Aucune notification. ✨
              </div>
            ) : (
              notifications.map((notification) => (
                <div
                  key={notification.id}
                  onClick={() => !notification.lu && markAsRead(notification.id)}
                  style={{
                    padding: '12px 15px',
                    borderBottom: '1px solid #f0f0f0',
                    background: notification.lu ? 'white' : '#f8f9ff',
                    cursor: notification.lu ? 'default' : 'pointer',
                    borderLeft: notification.lu ? 'none' : `4px solid ${getNotificationColor(notification.type)}`
                  }}
                >
                  <div style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '10px'
                  }}>
                    <span style={{ fontSize: '16px' }}>
                      {getNotificationIcon(notification.type)}
                    </span>
                    <div style={{ flex: 1 }}>
                      <div style={{
                        fontWeight: notification.lu ? 'normal' : 'bold',
                        fontSize: '14px',
                        marginBottom: '3px'
                      }}>
                        {notification.titre}
                      </div>
                      <div style={{
                        fontSize: '13px',
                        color: '#6c757d',
                        marginBottom: '5px'
                      }}>
                        {notification.message}
                      </div>
                      <div style={{
                        fontSize: '11px',
                        color: '#999'
                      }}>
                        {formatDate(notification.created_at)}
                        {notification.formulaire_id && (
                          <span style={{ marginLeft: '10px' }}>
                            📋 Formulaire #{notification.formulaire_id}
                          </span>
                        )}
                      </div>
                    </div>
                    {!notification.lu && (
                      <div style={{
                        width: '8px',
                        height: '8px',
                        background: getNotificationColor(notification.type),
                        borderRadius: '50%',
                        marginTop: '4px'
                      }} />
                    )}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          <div style={{
            padding: '10px 15px',
            borderTop: '1px solid #dee2e6',
            background: '#f8f9fa',
            borderRadius: '0 0 8px 8px',
            textAlign: 'center'
          }}>
            <button
              onClick={fetchNotifications}
              style={{
                background: 'none',
                border: 'none',
                color: '#9cb85c',
                cursor: 'pointer',
                fontSize: '12px'
              }}
            >
              🔄 Actualiser
            </button>
          </div>
        </div>
      )}

      {/* Overlay pour fermer les notifications */}
      {showNotifications && (
        <div
          onClick={() => setShowNotifications(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 999
          }}
        />
      )}
    </div>
  );
}

export default NotificationsPanel;
